import type { ReactNode } from "react";

export interface SettingsTileCardProps {
  /** Tile key — drives the `settings-tile-card-<key>` accent hook (host may theme per tile). */
  tileKey: string;
  /** Icon node, rendered inside the tinted icon chip (e.g. an @fawkes/ui icon or an `<img>`). */
  icon: ReactNode;
  /** Already-resolved tile title (i18n stays in the host). */
  title: string;
  /** Already-resolved one-line description. */
  description: string;
  /** Already-resolved sub-item labels, listed under the description. */
  items: string[];
  /** Already-resolved call-to-action label (e.g. "Openen ›"). */
  openLabel: string;
  onOpen: () => void;
}

/**
 * The presentational settings-dashboard tile card — a tinted icon chip, title, description, a column
 * of sub-item labels, and an "Openen ›" call-to-action. Reused by every configuration-group tile.
 *
 * Prop-driven and i18n-agnostic: the host resolves all strings and passes them in (so @fawkes/ui
 * stays free of the app's translation runtime). Visual ground truth is the Jmix `.settings-tile-card`
 * (themes/backoffice/view/settings-view.css); the styles live in ui.css, token-driven.
 */
export function SettingsTileCard({
  tileKey,
  icon,
  title,
  description,
  items,
  openLabel,
  onOpen,
}: SettingsTileCardProps) {
  return (
    <button
      type="button"
      className={`settings-tile-card settings-tile-card-${tileKey}`}
      onClick={onOpen}
    >
      <div className="settings-tile-card-content">
        <div className="settings-tile-body">
          <span className="settings-tile-icon-wrap">{icon}</span>
          <span className="settings-tile-title">{title}</span>
          <span className="settings-tile-description">{description}</span>
          <span className="settings-tile-bullets">
            {items.map((item) => (
              <span key={item} className="settings-tile-bullet">
                {item}
              </span>
            ))}
          </span>
        </div>
        <span className="settings-tile-cta">{openLabel}</span>
      </div>
    </button>
  );
}
